'use client';

import { format, parseISO } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import * as motion from 'motion/react-client';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { NewsletterForm } from '@/components/NewsletterForm';
import type { Post } from '@/types/post';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

// Topics for the sidebar
const topics = [
  { name: 'All posts', slug: null },
  { name: 'GTM Strategy', slug: 'gtm' },
  { name: 'Growth', slug: 'growth' },
  { name: 'Paid Media', slug: 'paid-media' },
  { name: 'Funnels', slug: 'funnels' },
  { name: 'Founder Life', slug: 'founder' },
];

interface BlogFiltersProps {
  posts: Post[];
}

export function BlogFilters({ posts }: BlogFiltersProps) {
  const searchParams = useSearchParams();
  const selectedTopic = searchParams.get('topic');

  // Filter posts based on selected topic
  const filteredPosts = selectedTopic
    ? posts.filter((post) =>
        post.frontmatter.tags?.some(
          (tag) => tag.toLowerCase() === selectedTopic.toLowerCase()
        )
      )
    : posts;

  const [featuredPost, ...otherPosts] = filteredPosts;

  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
      {/* Sidebar */}
      <motion.aside
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="lg:col-span-3"
      >
        <div className="lg:sticky lg:top-24">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-4">
            Topics
          </p>
          <nav className="space-y-2">
            {topics.map((topic) => (
              <Link
                key={topic.name}
                href={topic.slug ? `/blog?topic=${topic.slug}` : '/blog'}
                scroll={false}
                className={`block text-sm transition-colors ${
                  selectedTopic === topic.slug
                    ? 'text-gray-900 font-medium'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {topic.name}
              </Link>
            ))}
          </nav>

          {/* Subscribe CTA */}
          <div className="mt-10 pt-8 border-t border-gray-100">
            <div className="bg-gradient-to-b from-gray-50 to-white rounded-xl p-5 border border-gray-100">
              <p className="text-xs font-medium uppercase tracking-wider text-amber-600 mb-2">
                Free Newsletter
              </p>
              <h3
                className="text-lg text-gray-900 leading-tight"
                style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
              >
                Get growth insights
              </h3>
              <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                Join founders getting GTM strategies every week.
              </p>

              <div className="mt-4">
                <NewsletterForm variant="compact" />
              </div>
              <p className="mt-3 text-[10px] text-gray-400 text-center">
                No spam. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </motion.aside>

      {/* Articles */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={container}
        className="lg:col-span-9"
        key={selectedTopic ?? 'all'}
      >
        {/* Featured article */}
        {featuredPost && (
          <motion.article variants={item} className="mb-16">
            <Link href={`/blog/${featuredPost.slug}`} className="group block">
              <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
                {/* Visual element - abstract gradient */}
                <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 via-gray-50 to-white rounded-lg flex items-center justify-center overflow-hidden group-hover:from-gray-50 transition-all duration-500">
                  <span
                    className="text-[12rem] font-light text-gray-200/60 select-none"
                    style={{
                      fontFamily: 'var(--font-instrument), Georgia, serif',
                    }}
                  >
                    01
                  </span>
                </div>

                {/* Content */}
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2 py-1 bg-gray-100 text-xs font-medium text-gray-600 rounded">
                      Featured
                    </span>
                    <time
                      dateTime={featuredPost.frontmatter.date}
                      className="text-sm text-gray-400"
                    >
                      {format(
                        parseISO(featuredPost.frontmatter.date),
                        'MMM d, yyyy'
                      )}
                    </time>
                  </div>

                  <h3
                    className="text-2xl lg:text-3xl font-normal text-gray-900 leading-snug group-hover:text-gray-600 transition-colors duration-300"
                    style={{
                      fontFamily: 'var(--font-instrument), Georgia, serif',
                    }}
                  >
                    {featuredPost.frontmatter.title}
                  </h3>

                  <p className="mt-4 text-gray-500 leading-relaxed">
                    {featuredPost.frontmatter.description}
                  </p>

                  <div className="mt-6 flex items-center gap-2 text-sm text-gray-900 group-hover:text-gray-600 transition-colors">
                    <span>Read article</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.article>
        )}

        {/* Divider */}
        {filteredPosts.length > 0 && (
          <div className="border-t border-gray-200 mb-12" />
        )}

        {/* Other articles - grid */}
        {otherPosts.length > 0 && (
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-14">
            {otherPosts.map((post, index) => (
              <motion.article key={post.slug} variants={item}>
                <Link href={`/blog/${post.slug}`} className="group block">
                  {/* Small number accent */}
                  <span className="text-sm font-mono text-gray-300 mb-3 block">
                    {String(index + 2).padStart(2, '0')}
                  </span>

                  <h3
                    className="text-xl font-normal text-gray-900 leading-snug group-hover:text-gray-600 transition-colors duration-300"
                    style={{
                      fontFamily: 'var(--font-instrument), Georgia, serif',
                    }}
                  >
                    {post.frontmatter.title}
                  </h3>

                  <p className="mt-3 text-gray-500 leading-relaxed text-sm line-clamp-2">
                    {post.frontmatter.description}
                  </p>

                  <div className="mt-4 flex items-center gap-4 text-xs text-gray-400">
                    <time dateTime={post.frontmatter.date}>
                      {format(parseISO(post.frontmatter.date), 'MMM d, yyyy')}
                    </time>
                    <span>·</span>
                    <span>{post.readingTime}</span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {/* Empty state */}
        {filteredPosts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-24 text-center"
          >
            <p className="text-gray-400">
              {selectedTopic
                ? `No articles found for this topic.`
                : 'No articles yet.'}
            </p>
            {selectedTopic && (
              <Link
                href="/blog"
                className="mt-4 inline-block text-sm text-gray-600 hover:text-gray-900 underline"
              >
                View all posts
              </Link>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
